/**
 * Global timezone auto-detector.
 *
 * Runs whenever we have an authenticated user without a stored `timezone`.
 * Uses the browser API `Intl.DateTimeFormat().resolvedOptions().timeZone`
 * (IANA), then silently PATCHes the current user's profile.
 *
 * Priority (per V1.0 requirement):
 *   1. Stored user timezone (already on profile) — skip
 *   2. Browser detected IANA timezone — save silently
 *   3. IP-based suggestion — deliberately NOT called; deferred
 *
 * Idempotent + silent (no toast, no UI change).
 */
import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function TimezoneAutoDetector() {
  const { user, checkAuth } = useAuth();
  const savedRef = useRef(false);

  useEffect(() => {
    if (!user || user === false || savedRef.current) return;
    if (user.timezone) return; // already stored

    let detected;
    try {
      detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      detected = null;
    }
    if (!detected) return;

    savedRef.current = true;
    axios
      .patch(
        `${BACKEND_URL}/api/me/timezone`,
        { timezone: detected, confirmed: true },
        { withCredentials: true },
      )
      .then(() => {
        // Refresh user object so AuthContext reflects the new timezone.
        try { checkAuth && checkAuth(); } catch { /* noop */ }
      })
      .catch(() => {
        // Silent failure — will retry on next mount.
        savedRef.current = false;
      });
  }, [user, checkAuth]);

  return null;
}
